import {
  useCallback,
  useEffect,
  useRef,
  useReducer,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";

import { fetchProfile } from "../../redux/authSlice";
import { getAllProcedureParts } from "../../redux/partSlice";
import {
  createProcedureRevisionMOS,
  getProcedureRevisionsForPart,
} from "../../redux/procedureRevisionSlice";
import { getProcedureTemplateRuleById } from "../../redux/procedureTemplateRulesSlice";
import { getAllTypes } from "../../redux/typeSlice";
import { getAllUnits } from "../../redux/unitSlice";
import {
  fetchApprovedUsers,
  fetchReleasedUsers,
  fetchSubmittedUsers,
} from "../../redux/userSlice";

import "./AddProcedure.css";

import PartsPopup from "../../common/AppSpecificConpnents/PartsTable/PartsPopup";
import { showSnackbar } from "../../redux/snackbarSlice";

import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChangesBlocker";
import Loader from "../../common/Loader";
import RenderHtml from "../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import InputWithPen from "../../common/Inputs/InputWithPen";
import CreateNewRevisionModal, {
  MODAL_MODES,
} from "../../common/Modals/UnifiedRevisionModal";
import RevisionTable from "./RevisionTable";
import { buildPermissionContext } from "../../util/permissions";

// Action types for useReducer
const ACTION_TYPES = {
  SET_LOADING: "SET_LOADING",
  SET_CURRENT_PART_INFO: "SET_CURRENT_PART_INFO",
  SET_PARTS_STATE: "SET_PARTS_STATE",
  SET_MODAL_STATE: "SET_MODAL_STATE",
  SET_FORM_DATA: "SET_FORM_DATA",
  SET_USER_SELECTIONS: "SET_USER_SELECTIONS",
  SET_SEARCH_STATE: "SET_SEARCH_STATE",
  SET_UNSAVED_CHANGES: "SET_UNSAVED_CHANGES",
  RESET_FORM: "RESET_FORM",
};

// Initial state structure
const initialState = {
  // Loading states
  showLoader: false,
  isCreateRevisionLoading: false,

  // Current part info
  currentPartInfo: {},
  templateProcedureRevisionId: null,

  // Parts table state
  parts: {
    offset: 0,
    limit: 100,
    isLoadingMore: false,
    allLoaded: false,
    scrollPosition: 0,
  },

  // Modal states
  modals: {
    isModalOpen: false,
    isPartsModalOpen: false,
    revisionTableModalOpen: false,
  },

  // Form data
  form: {
    revisionDetails: "",
    revisionDetailsForForm: "",
    references: "",
    description: "",
    procedureNumber: "",
    revision: "A",
    template: "",
    partId: "",
    procedureType: "",
    password: "",
    signature: "",
  },

  // User selections
  users: {
    submissionBy: "",
    releasedBy: "",
    approvedBy: [],
  },

  // Search and filter state
  search: {
    query: "",
    type: "all_columns",
    selectedColumns: {
      type: true,
      part_number: true,
      description: true,
      table_note: true,
      textbox_note: true,
      default_unit: true,
      url: true,
    },
    isSearching: false,
    lastSearchQuery: "",
    isModalInitialized: false,
    hasAttachedScrollListener: false,
  },

  // Other states
  hasUnsavedChanges: false,
  selectedTemplateRef: {},
};

// Reducer function
const procedureReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        [action.field]: action.value,
      };

    case ACTION_TYPES.SET_CURRENT_PART_INFO:
      return {
        ...state,
        currentPartInfo: action.value,
        templateProcedureRevisionId: action.templateId || null,
      };

    case ACTION_TYPES.SET_PARTS_STATE:
      return {
        ...state,
        parts: {
          ...state.parts,
          ...action.value,
        },
      };

    case ACTION_TYPES.SET_MODAL_STATE:
      return {
        ...state,
        modals: {
          ...state.modals,
          ...action.value,
        },
      };

    case ACTION_TYPES.SET_FORM_DATA:
      return {
        ...state,
        form: {
          ...state.form,
          ...action.value,
        },
        hasUnsavedChanges: true,
      };

    case ACTION_TYPES.SET_USER_SELECTIONS:
      return {
        ...state,
        users: {
          ...state.users,
          ...action.value,
        },
      };

    case ACTION_TYPES.SET_SEARCH_STATE:
      return {
        ...state,
        search: {
          ...state.search,
          ...action.value,
        },
      };

    case ACTION_TYPES.SET_UNSAVED_CHANGES:
      return {
        ...state,
        hasUnsavedChanges: action.value,
      };

    case ACTION_TYPES.RESET_FORM:
      return {
        ...state,
        form: {
          ...initialState.form,
          revision: state.form.revision, // Keep current revision
        },
        currentPartInfo: {},
        templateProcedureRevisionId: null,
        selectedTemplateRef: {},
        hasUnsavedChanges: false,
      };

    default:
      return state;
  }
};

const AddProcedure = () => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(procedureReducer, initialState);

  const { revisionData, revisionsForPart } = useSelector(
    (state) => state.procedureRevision
  );
  const { rule } = useSelector((state) => state.procedureTemplateRule);
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canCreate = permissionCtx.permissions?.Create === true;
  const canView = permissionCtx.permissions?.View === true;
  // These state variables are now handled by useReducer

  const partTableContnrRef = useRef(null);

  const formatName = useCallback((rev, user, isApprover) => {
    if (rev.is_maintained_outside_system) {
      return "MAINTAINED OUTSIDE OF SYSTEM";
    } else {
      if (isApprover) {
        return user.map((u) => `${u.first_name} ${u.last_name}`).join(", ");
      } else {
        return user ? `${user.first_name} ${user.last_name}` : "-";
      }
    }
  }, []);

  const formatDateForInput = useCallback((date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const sigPadRef = useRef();
  const reduxDispatch = useDispatch();

  // Auto-fill the modal's "Enter The Revision Details" from the main
  // "Revision Details" field when the modal opens (and keep in sync while open).
  useEffect(() => {
    if (state.modals.isModalOpen) {
      dispatch({
        type: ACTION_TYPES.SET_FORM_DATA,
        value: { revisionDetailsForForm: state.form.revisionDetails },
      });
    }
  }, [state.modals.isModalOpen, state.form.revisionDetails]);

  // Intercept AppBar back navigation
  const handleAppBarBack = useCallback(() => {
    // Let the useUnsavedChangesBlocker handle the confirmation
    // Don't show manual confirmation here to avoid conflicts
    navigate(-1);
  }, [navigate]);

  useUnsavedChangesBlocker(state.hasUnsavedChanges);

  // Restore snapshot when returning from Preview
  useEffect(() => {
    try {
      const key = sessionStorage.getItem("addProcedure_restoreKey");
      if (!key) return;
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const snap = JSON.parse(raw);
      if (snap?.form) {
        dispatch({ type: ACTION_TYPES.SET_FORM_DATA, value: { ...snap.form } });
      }
      if (snap?.currentPartInfo) {
        dispatch({
          type: ACTION_TYPES.SET_CURRENT_PART_INFO,
          value: snap.currentPartInfo,
        });
      }
      if (snap?.users) {
        dispatch({ type: ACTION_TYPES.SET_USER_SELECTIONS, value: snap.users });
      }
      // cleanup
      localStorage.removeItem(key);
      sessionStorage.removeItem("addProcedure_restoreKey");
    } catch (e) {
      console.warn("Failed restoring AddProcedure preview snapshot", e);
    }
  }, [dispatch]);

  const snackbar = (message, type) => {
    reduxDispatch(showSnackbar({ type: type, message: message }));
  };

  const {
    submittedUsers: users,
    approvedUsers,
    releasedUsers,
  } = useSelector((state) => state.users);

  useEffect(() => {
    if (users.length > 0 && !state.users.submissionBy) {
      dispatch({
        type: ACTION_TYPES.SET_USER_SELECTIONS,
        value: { submissionBy: users[0].id },
      });
    }
  }, [users, state.users.submissionBy]);

  useEffect(() => {
    if (users.length > 0 && state.users.approvedBy.length === 0) {
      dispatch({
        type: ACTION_TYPES.SET_USER_SELECTIONS,
        value: { approvedBy: [users[0].id] },
      });
    }
  }, [users, state.users.approvedBy]);

  useEffect(() => {
    if (users.length > 0 && !state.users.releasedBy) {
      dispatch({
        type: ACTION_TYPES.SET_USER_SELECTIONS,
        value: { releasedBy: users[0].id },
      });
    }
  }, [users, state.users.releasedBy]);

  const handleEndDrawing = () => {
    const dataUrl = sigPadRef.current.getCanvas().toDataURL("image/png");
    dispatch({
      type: ACTION_TYPES.SET_FORM_DATA,
      value: { signature: dataUrl },
    });
  };

  const { loading } = useSelector((state) => state.procedureRevision);

  useEffect(() => {
    if (!rule) return;

    if (rule.is_blank_template_permitted) {
      dispatch({
        type: ACTION_TYPES.SET_FORM_DATA,
        value: { template: "Blank" },
      });
    } else {
      const defaultTem = rule.parts?.find((ru) => ru.is_default_template);
      if (defaultTem) {
        dispatch({
          type: ACTION_TYPES.SET_FORM_DATA,
          value: { template: defaultTem.part_id || "" },
        });
        dispatch({
          type: ACTION_TYPES.SET_CURRENT_PART_INFO,
          value: state.currentPartInfo,
          templateId:
            defaultTem.latest_procedure_revision?.procedure_revision_id || null,
        });
      }
    }
  }, [rule, state.currentPartInfo]);
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Use Promise.all to fetch all data in parallel
        await Promise.all([
          reduxDispatch(getAllProcedureParts({ limit: 10000, offset: 0 })),
          reduxDispatch(fetchProfile()),
          reduxDispatch(fetchSubmittedUsers({ limit: 100, offset: 0 })),
          reduxDispatch(fetchReleasedUsers({ limit: 100, offset: 0 })),
          reduxDispatch(fetchApprovedUsers({ limit: 100, offset: 0 })),
          reduxDispatch(getAllUnits({ limit: 1000, offset: 0 })),
          reduxDispatch(getAllTypes({ limit: 1000, offset: 0 })),
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, [reduxDispatch]); // Only depend on reduxDispatch

  // Lightweight Preview handler – routes to shared /preview retaining UI
  const handlePreviewClick = () => {
    // Validate mandatory fields before preview
    if (!state.form.procedureNumber || !state.form.partId) {
      snackbar("Please fill the mandatory fields", "error");
      return;
    }
    const partNumber =
      state.form.procedureNumber || state.currentPartInfo.part_number || "";
    const type =
      state.form.procedureType ||
      state.currentPartInfo.type_name ||
      state.currentPartInfo.type ||
      "";

    // Latest revision label for header (fallback to A-)
    const latestRevision = (() => {
      if (!revisionData || revisionData.length === 0) return "A-";
      const sorted = [...revisionData].sort((a, b) => {
        const ra = parseRevision(a.rev);
        const rb = parseRevision(b.rev);
        if (!ra && !rb) return 0;
        if (!ra) return 1;
        if (!rb) return -1;
        if (ra.type === "special") return -1;
        if (rb.type === "special") return 1;
        if (ra.type === "special_chars") return -1;
        if (rb.type === "special_chars") return 1;
        if (ra.base !== rb.base) return ra.base.localeCompare(rb.base);
        if (ra.type === "major" && rb.type !== "major") return -1;
        if (rb.type === "major" && ra.type !== "major") return 1;
        if (ra.minorNum !== rb.minorNum) return ra.minorNum - rb.minorNum;
        return ra.amendChar.localeCompare(rb.amendChar);
      });
      return sorted[sorted.length - 1]?.rev || "A-";
    })();

    // Compute page status text like screenshot
    const statusText =
      revisionData?.[0]?.status_changes?.[0]?.status_changed_to ||
      "Not Submitted";

    // Build simple strings for header (consistent with other previews)
    const releasedText = "-"; // no release yet in Add flow
    const basedOnText = "-";

    // Persist current form data so it can be restored when returning from preview
    try {
      const formSnapshot = {
        form: { ...state.form },
        currentPartInfo: { ...state.currentPartInfo },
        users: { ...state.users },
      };
      const storageKey = `addProcedure_${Date.now()}`;
      localStorage.setItem(storageKey, JSON.stringify(formSnapshot));
      sessionStorage.setItem("addProcedure_restoreKey", storageKey);
      // Build compact preview payload to render same UI in preview
      const procedurePreviewData = {
        procedureNumber:
          state.form.procedureNumber || state.currentPartInfo.part_number || "",
        procedureType:
          state.form.procedureType ||
          state.currentPartInfo.type_name ||
          state.currentPartInfo.type ||
          "",
        description:
          state.form.description || state.currentPartInfo.description || "",
        references: state.form.references || "",
        revisionDetails: state.form.revisionDetails || "",
        partId: state.form.partId || state.currentPartInfo.part_id || "",
        revisionData: revisionData || [],
        storageKey,
      };

      // Navigate to existing Preview page with explicit procedure preview type
      navigate("/preview", {
        state: {
          previewType: "procedure",
          procedurePreviewData,
          status: statusText,
          partNumber,
          type,
          description: procedurePreviewData.description,
          revisionData: revisionData,
          revision: latestRevision,
          releasedText,
          basedOnText,
          element: {},
        },
      });
      return;
    } catch (e) {
      console.warn("Failed saving AddProcedure preview snapshot", e);
    }
  };

  // Handle Save Button Click (Open Modal)
  const handleSaveClick = () => {
    // Validate mandatory fields before opening modal
    if (
      !state.form.procedureNumber ||
      state.form.procedureNumber.trim() === ""
    ) {
      snackbar("Please fill the Document Number (mandatory field).", "error");
      return;
    }

    // if (!state.form.description || state.form.description.trim() === "") {
    //   snackbar("Please fill the Document Description (mandatory field).", "error");
    //   return;
    // }

    if (revisionData.length === 0) {
      dispatch({
        type: ACTION_TYPES.SET_MODAL_STATE,
        value: { isModalOpen: true },
      });
      return;
    }
    const sorted = sortedRevisions;

    const lastRev = sorted[sorted.length - 1]?.rev || "";
    const parsed = parseRevision(lastRev);

    const isAmendedMajor = /^[A-Z]{1,3}-[a-z]+$/.test(lastRev);

    if (!parsed) {
      snackbar("Can't create a new revision with an empty revision.", "error");
      return;
    }

    if (parsed.type === "special" || isAmendedMajor) {
      snackbar(
        "This revision is invalid and cannot be used to create a new revision.",
        "error"
      );
      return;
    }

    dispatch({
      type: ACTION_TYPES.SET_MODAL_STATE,
      value: { isModalOpen: true },
    });
  };

  // Set first radio button as selected when modal opens
  useEffect(() => {
    if (state.modals.isModalOpen && revisionData.length > 0) {
      const sorted = [...revisionData].sort((a, b) => {
        const revA = parseRevision(a.rev);
        const revB = parseRevision(b.rev);
        if (!revA && !revB) return 0;
        if (!revA) return 1;
        if (!revB) return -1;
        if (revA.type === "special") return -1;
        if (revB.type === "special") return 1;
        if (revA.base !== revB.base) return revA.base.localeCompare(revB.base);
        if (revA.type === "major" && revB.type !== "major") return -1;
        if (revB.type === "major" && revA.type !== "major") return 1;
        if (revA.minorNum !== revB.minorNum)
          return revA.minorNum - revB.minorNum;
        return revA.amendChar.localeCompare(revB.amendChar);
      });

      const lastValidRevision = [...sorted]
        .reverse()
        .find(
          (rev) =>
            !["rejected", "external"].includes(
              rev.status_changes?.[0]?.status_changed_to?.toLowerCase() || ""
            )
        );

      if (lastValidRevision) {
        const lastRevParsed = parseRevision(lastValidRevision.rev || "A0");
        if (lastRevParsed) {
          const getNextLetter = (str) => {
            const lastChar = str[str.length - 1];
            const rest = str.slice(0, -1);
            const nextChar = String.fromCharCode(lastChar.charCodeAt(0) + 1);
            if (lastChar === "Z") return rest + "A";
            return rest + nextChar;
          };

          const nextMajorBase =
            lastRevParsed.base.length === 2
              ? getNextLetter(lastRevParsed.base)
              : getNextLetter(lastRevParsed.base || "A");

          const majorRev = `${nextMajorBase}-`;

          // Set the first available option (Major revision) as default
          dispatch({
            type: ACTION_TYPES.SET_FORM_DATA,
            value: { revision: majorRev },
          });
        }
      }
    } else if (state.modals.isModalOpen && revisionData.length === 0) {
      // If no revision data, set to "A-" as default
      dispatch({
        type: ACTION_TYPES.SET_FORM_DATA,
        value: { revision: "A-" },
      });
    }
  }, [state.modals.isModalOpen, revisionData]);

  // Set first template radio button as selected when modal opens
  useEffect(() => {
    if (state.modals.isModalOpen && rule) {
      // If blank template is permitted, set it as default
      if (rule.is_blank_template_permitted) {
        dispatch({
          type: ACTION_TYPES.SET_FORM_DATA,
          value: { template: "Blank" },
        });
        dispatch({
          type: ACTION_TYPES.SET_CURRENT_PART_INFO,
          value: state.currentPartInfo,
          templateId: null,
        });
      } else if (rule.parts && rule.parts.length > 0) {
        // Find the default template or use the first one
        const defaultTemplate = rule.parts.find(
          (part) => part.is_default_template
        );
        const firstTemplate = rule.parts[0];
        const selectedTemplate = defaultTemplate || firstTemplate;

        if (selectedTemplate) {
          dispatch({
            type: ACTION_TYPES.SET_CURRENT_PART_INFO,
            value: {
              ...state.currentPartInfo,
              selectedTemplateRef: selectedTemplate,
            },
            templateId:
              selectedTemplate.latest_procedure_revision
                ?.procedure_revision_id || null,
          });
          dispatch({
            type: ACTION_TYPES.SET_FORM_DATA,
            value: { template: selectedTemplate.part_id },
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.modals.isModalOpen, rule]);
  // Handle Close Modal
  const handleCloseModal = () => {
    dispatch({
      type: ACTION_TYPES.SET_MODAL_STATE,
      value: { isPartsModalOpen: false },
    });
  };

  // Handle revision creation success
  const handleRevisionCreated = useCallback(
    (createdRevisionId) => {
      // Refresh the revisions data
      if (state.form.partId) {
        reduxDispatch(
          getProcedureRevisionsForPart({ partId: state.form.partId })
        );
      }
    },
    [state.form.partId, reduxDispatch]
  );

  const onEditClick = (revisionId, revision, status, myPartId, typeName) => {
    switch (status) {
      case "Not Submitted":
        navigate(
          `/revision-submission/${state.form.partId || myPartId}/${revision}/${
            state.form.procedureType
          }/${state.currentPartInfo.part_number}/${revisionId}/${
            state.currentPartInfo.description
          }`,
          { state: { status: "Not Submitted" } }
        );
        break;
      case "Change Requested":
        navigate(
          `/revision-submission/${state.form.partId || myPartId}/${revision}/${
            state.form.procedureType
          }/${state.currentPartInfo.part_number}/${revisionId}/${
            state.currentPartInfo.description
          }`,
          { state: { status: "Change Requested" } }
        );
        break;
      case "Submitted For Approval":
        navigate(
          `/approve-revision/${state.form.partId || myPartId}/${revision}/${
            state.form.procedureType
          }/${state.currentPartInfo.part_number}/${revisionId}/${
            state.currentPartInfo.description
          }`
        );
        break;
      case "Approved":
        navigate(
          `/release-revision/${state.form.partId || myPartId}/${revision}/${
            state.form.procedureType
          }/${state.currentPartInfo.part_number}/${revisionId}/${
            state.currentPartInfo.description
          }`
        );
        break;
      case "Released":
        navigate(
          `/released-revision/${state.form.partId || myPartId}/${revision}/${
            state.form.procedureType
          }/${state.currentPartInfo.part_number}/${
            state.currentPartInfo.description
          }/${revisionId}`
        );
        break;
      case "Rejected":
        navigate(
          `/rejected-revision/${state.form.partId || myPartId}/${revision}/${
            state.form.procedureType
          }/${state.currentPartInfo.part_number}/${
            state.currentPartInfo.description
          }/Rejected/${revisionId}`
        );
        break;
      case "Archived":
        navigate(
          `/archived-revision/${state.form.partId || myPartId}/${revision}/${
            state.form.procedureType
          }/${state.currentPartInfo.part_number}/${
            state.currentPartInfo.description
          }/Archived/${revisionId}`
        );
        break;
      default:
    }
  };

  const createRevisionForRevisionTable = async (row) => {
    if (state.form.procedureNumber.length === 0) {
      snackbar("Please select a procedure number to proceed.", "error");
      return;
    }

    const revisionData = {
      rev: row.rev,
      revision_details: row.revision_details,
      part_ref: state.form.partId,
      is_latest: true,
      is_maintained_outside_system: true,
      status_changed_to: "MAINTAINED OUTSIDE OF SYSTEM",
      is_in_release_history: false,
      past_procedure_revisions_reviewed: {},
      required_by_details: [],
      reference: row.reference,
      date_released: row.date_released,
    };

    try {
      dispatch({
        type: ACTION_TYPES.SET_LOADING,
        field: "showLoader",
        value: true,
      });
      const payload = await reduxDispatch(
        createProcedureRevisionMOS({
          revisionData: revisionData,
          version_content: {
            action_to_show: `Created ${state.form.procedureNumber} Rev ${row.rev}`,
            current_status: "MAINTAINED OUTSIDE OF SYSTEM",
            procedure_number: state.form.procedureNumber,
            type: state.form.procedureType,
            description: state.form.description,
            requested_by: localStorage.getItem("user_name"),
            ...revisionData,
          },
        })
      ).unwrap();
      dispatch({
        type: ACTION_TYPES.SET_LOADING,
        field: "showLoader",
        value: false,
      });

      const createdRevisionId =
        payload?.results?.tableOfProcedures?.procedure_revision_ref;

      if (!createdRevisionId) {
        throw new Error("Missing revision ID in response.");
      }

      snackbar("Revision created successfully!", "success");
      dispatch({
        type: ACTION_TYPES.SET_MODAL_STATE,
        value: { isModalOpen: false },
      });
      await reduxDispatch(
        getProcedureRevisionsForPart({ partId: state.form.partId })
      );

      // navigate(
      //   `/revision-submission/${state.form.partId}/${row.rev}/${state.form.procedureType}/${state.form.procedureNumber}/${createdRevisionId}/${state.form.description}`
      // );
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.SET_LOADING,
        field: "showLoader",
        value: false,
      });
      snackbar("Error creating revision: " + error, "error");
    }
  };

  const handleOpenModal = () => {
    dispatch({
      type: ACTION_TYPES.SET_MODAL_STATE,
      value: { isPartsModalOpen: true },
    });
  };

  const handleSelectPart = async (selectedPart) => {
    dispatch({
      type: ACTION_TYPES.SET_CURRENT_PART_INFO,
      value: selectedPart,
    });
    dispatch({
      type: ACTION_TYPES.SET_FORM_DATA,
      value: {
        procedureNumber: selectedPart.part_number,
        procedureType: selectedPart.type_details?.name,
        partId: selectedPart.part_id,
        description: selectedPart.description,
      },
    });
    handleCloseModal();
    if (selectedPart?.type_id_ref) {
      await reduxDispatch(
        getProcedureTemplateRuleById(selectedPart.type_id_ref)
      );
    }
    await reduxDispatch(
      getProcedureRevisionsForPart({ partId: selectedPart.part_id })
    );

    dispatch({
      type: ACTION_TYPES.SET_UNSAVED_CHANGES,
      value: true,
    });
    // Close modal after selection
  };

  // Search-related functions using dispatch

  // Replace the modal open/close effect with this strict version
  useEffect(() => {
    // Only run this effect when the modal opens or closes
    // if (isPartsModalOpen) {
    //   // Reset search state
    //   setSearchQuery("");
    //   setLastSearchQuery("");
    //   // Only initialize if not already initialized
    //   if (!isModalInitialized) {
    //     const initializeModal = async () => {
    //       try {
    //         setIsLoading(true);
    //         // Make a single API call to get initial data
    //         await dispatch(
    //           getAllProcedureParts({
    //             limit: 10000,
    //             offset: 0,
    //             is_procedure: true,
    //           })
    //         ).unwrap();
    //         setIsModalInitialized(true);
    //       } catch (error) {
    //         console.error("Error initializing modal:", error);
    //       } finally {
    //         setIsLoading(false);
    //       }
    //     };
    //     initializeModal();
    //   }
    // } else {
    //   // Reset when modal is closed
    //   setIsModalInitialized(false);
    //   setPartsOffset(0);
    //   setAllPartsLoaded(false);
    //   // setLocalProcedureParts([]);
    //   setHasAttachedScrollListener(false);
    // }
  }, [
    dispatch,
    state.search.isModalInitialized,
    state.modals.isPartsModalOpen,
  ]); // ONLY depend on isPartsModalOpen

  // Replace the search effect with a strictly controlled version
  useEffect(() => {
    // Skip if modal is not open or we're already searching
    if (
      !state.modals.isPartsModalOpen ||
      state.search.isSearching ||
      !state.search.isModalInitialized
    )
      return;

    let searchTimeout;

    // Only run search if query changes
    if (state.search.query !== state.search.lastSearchQuery) {
      dispatch({
        type: ACTION_TYPES.SET_SEARCH_STATE,
        value: { isSearching: true },
      });

      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      searchTimeout = setTimeout(() => {
        const performSearch = async () => {
          try {
            dispatch({
              type: ACTION_TYPES.SET_PARTS_STATE,
              value: { isLoadingMore: true },
            });

            const params = {
              limit: state.parts.limit,
              offset: 0,
              is_procedure: true,
            };

            if (state.search.query && state.search.query.trim()) {
              if (state.search.selectedColumns.type) {
                params.type = state.search.query.trim();
              }
              if (state.search.selectedColumns.part_number) {
                params.partNumber = state.search.query.trim();
              }
            }

            const result = await reduxDispatch(
              getAllProcedureParts(params)
            ).unwrap();

            // setLocalProcedureParts(result || []);
            dispatch({
              type: ACTION_TYPES.SET_PARTS_STATE,
              value: {
                offset: 0,
                allLoaded: result?.length < state.parts.limit,
              },
            });
            dispatch({
              type: ACTION_TYPES.SET_SEARCH_STATE,
              value: { lastSearchQuery: state.search.query },
            });
          } catch (error) {
            console.error("Search error:", error);
          } finally {
            dispatch({
              type: ACTION_TYPES.SET_PARTS_STATE,
              value: { isLoadingMore: false },
            });
            dispatch({
              type: ACTION_TYPES.SET_SEARCH_STATE,
              value: { isSearching: false },
            });
          }
        };

        performSearch();
      }, 500);
    }

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [
    state.search.query,
    state.modals.isPartsModalOpen,
    state.search.isModalInitialized,
    state.search.lastSearchQuery,
    state.search.isSearching,
    state.parts.limit,
    reduxDispatch,
    state.search.selectedColumns.type,
    state.search.selectedColumns.part_number,
  ]);

  // Replace the scroll handler with a completely new implementation
  useEffect(() => {
    // Only attach scroll listener once when modal is initialized
    if (
      !state.modals.isPartsModalOpen ||
      !state.search.isModalInitialized ||
      state.search.hasAttachedScrollListener
    )
      return;

    dispatch({
      type: ACTION_TYPES.SET_SEARCH_STATE,
      value: { hasAttachedScrollListener: true },
    });

    const handlePartsScroll = () => {
      const container = partTableContnrRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;

      // Store scroll position but don't trigger rerender
      if (scrollTop !== state.parts.scrollPosition) {
        dispatch({
          type: ACTION_TYPES.SET_PARTS_STATE,
          value: { scrollPosition: scrollTop },
        });
      }

      // Check if scrolled to the bottom (with a threshold) and not already loading
      if (
        scrollTop + clientHeight >= scrollHeight - 200 &&
        !state.parts.isLoadingMore &&
        !state.parts.allLoaded &&
        !state.search.isSearching
      ) {
        const nextOffset = state.parts.offset + state.parts.limit;

        // Set loading state
        dispatch({
          type: ACTION_TYPES.SET_PARTS_STATE,
          value: { isLoadingMore: true },
        });

        // Load more data
        reduxDispatch(
          getAllProcedureParts({
            limit: state.parts.limit,
            offset: nextOffset,
            is_procedure: true,
            type: state.search.selectedColumns.type ? state.search.query : "",
            partNumber: state.search.selectedColumns.part_number
              ? state.search.query
              : "",
          })
        )
          .then((result) => {
            if (result.payload && result.payload.length > 0) {
              // Update offset for next page
              dispatch({
                type: ACTION_TYPES.SET_PARTS_STATE,
                value: { offset: nextOffset },
              });

              // Update local parts without duplicates
              // setLocalProcedureParts((prevParts) => {
              //   const existingIds = new Set(
              //     prevParts.map((part) => part.part_id)
              //   );
              //   const newParts = result.payload.filter(
              //     (part) => !existingIds.has(part.part_id)
              //   );
              //   return [...prevParts, ...newParts];
              // });
            } else {
              // No more data to load
              dispatch({
                type: ACTION_TYPES.SET_PARTS_STATE,
                value: { allLoaded: true },
              });
            }
          })
          .catch((error) => {
            console.error("Error loading more parts:", error);
          })
          .finally(() => {
            dispatch({
              type: ACTION_TYPES.SET_PARTS_STATE,
              value: { isLoadingMore: false },
            });
          });
      }
    };

    const tableContainer = partTableContnrRef.current;
    if (tableContainer) {
      // Use passive listener for better performance
      tableContainer.addEventListener("scroll", handlePartsScroll, {
        passive: true,
      });
    }

    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handlePartsScroll);
      }
    };
  }, [
    state.modals.isPartsModalOpen,
    state.search.isModalInitialized,
    state.search.hasAttachedScrollListener,
    state.parts.scrollPosition,
    state.parts.isLoadingMore,
    state.parts.allLoaded,
    state.search.isSearching,
    state.parts.offset,
    state.parts.limit,
    reduxDispatch,
    state.search.selectedColumns.type,
    state.search.selectedColumns.part_number,
    state.search.query,
  ]);

  // Remove any other effects that might be causing duplicate API calls
  useEffect(() => {
    if (
      state.search.selectedColumns.type ||
      state.search.selectedColumns.part_number
    ) {
      // Remove this effect entirely - we handle search in a dedicated effect
    }
  }, [
    state.search.selectedColumns.part_number,
    state.search.selectedColumns.type,
  ]);

  const parseRevision = (rev) => {
    if (!rev) return null;

    // Check for special characters in the revision
    const hasSpecialChars = /[@#$%!^&*]/.test(rev);
    if (hasSpecialChars) {
      return {
        base: "",
        type: "special_chars",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    if (rev === "-") {
      return {
        base: "",
        type: "special",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    if (/^[A-Z]{1,3}-$/.test(rev)) {
      return {
        base: rev.slice(0, -1),
        type: "major",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    const amendMatch = rev.match(/^([A-Z]{1,3})(\d+)-([a-z]+)$/);
    if (amendMatch) {
      const [, base, num, amendChar] = amendMatch;
      return {
        base,
        type: "amend",
        minorNum: parseInt(num),
        amendChar,
        original: rev,
      };
    }

    const minorMatch = rev.match(/^([A-Z]{1,3})(\d+)([a-z]*)$/);
    if (minorMatch) {
      const [, base, num, amendChar] = minorMatch;
      return {
        base,
        type: amendChar ? "amend" : "minor",
        minorNum: parseInt(num),
        amendChar: amendChar || "",
        original: rev,
      };
    }

    return null;
  };

  // Memoized revision sorting function
  const sortRevisions = useCallback((revisions) => {
    return [...revisions].sort((a, b) => {
      const revA = parseRevision(a.rev);
      const revB = parseRevision(b.rev);

      // If either revision can't be parsed, keep original order
      if (!revA && !revB) return 0;
      if (!revA) return 1;
      if (!revB) return -1;

      // Special case: "-" should always come first
      if (revA.type === "special") return -1;
      if (revB.type === "special") return 1;

      // Special case: revisions with special characters should come second
      if (revA.type === "special_chars") return -1;
      if (revB.type === "special_chars") return 1;

      // Compare base letters (A, B, C, etc.)
      if (revA.base !== revB.base) {
        return revA.base.localeCompare(revB.base);
      }

      // If same base, major revisions come before minor/amend
      if (revA.type === "major" && revB.type !== "major") return -1;
      if (revB.type === "major" && revA.type !== "major") return 1;

      // Compare minor numbers
      if (revA.minorNum !== revB.minorNum) {
        return revA.minorNum - revB.minorNum;
      }

      // If same minor number, amendments come after minors
      if (revA.type === "minor" && revB.type === "amend") return -1;
      if (revB.type === "minor" && revA.type === "amend") return 1;

      // Compare amendment characters
      return revA.amendChar.localeCompare(revB.amendChar);
    });
  }, []);

  // Memoized sorted revisions
  const sortedRevisions = useMemo(() => {
    return sortRevisions(revisionData);
  }, [revisionData, sortRevisions]);

  // Memoized last valid revision
  const lastValidRevision = useMemo(() => {
    if (sortedRevisions.length === 0) return null;

    return [...sortedRevisions]
      .reverse()
      .find(
        (rev) =>
          !["rejected", "external"].includes(
            rev.status_changes?.[0]?.status_changed_to?.toLowerCase() || ""
          )
      );
  }, [sortedRevisions]);

  // Memoized revision options for modal
  const revisionOptions = useMemo(() => {
    if (!lastValidRevision || !rule) return [];

    const lastRevParsed = parseRevision(lastValidRevision.rev || "A0");
    if (!lastRevParsed) return [];

    const fromRevStatus =
      lastValidRevision.status_changes?.[0]?.status_changed_to?.toLowerCase() ||
      "";

    const isReleasedOrArchived = ["released", "archived"].includes(
      fromRevStatus
    );

    const getNextLetter = (str) => {
      const lastChar = str[str.length - 1];
      const rest = str.slice(0, -1);
      const nextChar = String.fromCharCode(lastChar.charCodeAt(0) + 1);
      if (lastChar === "Z") return rest + "A";
      return rest + nextChar;
    };

    const nextMajorBase =
      lastRevParsed.base.length === 2
        ? getNextLetter(lastRevParsed.base)
        : getNextLetter(lastRevParsed.base || "A");

    const majorRev = `${nextMajorBase}-`;

    const minorRev =
      lastRevParsed.type === "major"
        ? `${lastRevParsed.base}1`
        : `${lastRevParsed.base}${lastRevParsed.minorNum + 1}`;

    const amendCharNext = String.fromCharCode(
      (lastRevParsed.amendChar?.charCodeAt(0) || 96) + 1
    );

    const amendRev =
      lastRevParsed.type === "major"
        ? `${lastRevParsed.base}-a`
        : `${lastRevParsed.base}${lastRevParsed.minorNum}${amendCharNext}`;

    const existingRevs = revisionData.map((r) => r.rev);

    const groupBySeries = (revisions) => {
      const series = {};
      for (const rev of revisions) {
        const parsed = parseRevision(rev.rev);
        if (!parsed) continue;
        const majorKey = parsed.base;
        const minorKey = `${parsed.base}${parsed.minorNum}`;
        series[majorKey] ??= [];
        series[majorKey].push(rev);
        if (parsed.type === "minor" || parsed.type === "amend") {
          series[minorKey] ??= [];
          series[minorKey].push(rev);
        }
      }
      return series;
    };

    const allRejected = (revList = []) =>
      revList.length > 0 &&
      revList.every(
        (r) =>
          r.status_changes?.[0]?.status_changed_to?.toLowerCase() === "rejected"
      );

    const series = groupBySeries([...revisionData]);
    const majorSeriesKey = lastRevParsed.base;
    const minorSeriesKey = `${lastRevParsed.base}${lastRevParsed.minorNum}`;

    const allowMinor = !allRejected(
      series[majorSeriesKey]?.filter(
        (r) => parseRevision(r.rev)?.type === "major"
      )
    );

    const allowAmend = !allRejected(
      series[minorSeriesKey]?.filter(
        (r) => parseRevision(r.rev)?.type === "minor"
      )
    );

    const options = [];

    // Always allow next Major revision
    options.push({
      label: `${majorRev} (Major)`,
      value: majorRev,
    });

    // Allow Minor only if last valid major isn't rejected
    if (allowMinor) {
      options.push({
        label: `${minorRev} (Minor)`,
        value: minorRev,
      });
    }

    // Allow Amendment only if the base is released or archived and not already amended
    if (
      lastRevParsed.type === "major" &&
      isReleasedOrArchived &&
      !existingRevs.includes(amendRev) &&
      allowAmend
    ) {
      options.push({
        label: `${amendRev} (Amend)`,
        value: amendRev,
      });
    }

    return options;
  }, [lastValidRevision, rule, revisionData]);

  const addProcedureAppBarTitle = (() => {
    if (!state.form.procedureType) return "New Document";
    const lastRev =
      revisionData.length === 0
        ? "A-"
        : (() => {
            const sorted = [...revisionData].sort((a, b) => {
              const revA = parseRevision(a.rev);
              const revB = parseRevision(b.rev);
              if (!revA && !revB) return 0;
              if (!revA) return 1;
              if (!revB) return -1;
              if (revA.type === "special") return -1;
              if (revB.type === "special") return 1;
              if (revA.type === "special_chars") return -1;
              if (revB.type === "special_chars") return 1;
              if (revA.base !== revB.base)
                return revA.base.localeCompare(revB.base);
              if (revA.type === "major" && revB.type !== "major") return -1;
              if (revB.type === "major" && revA.type !== "major") return 1;
              if (revA.minorNum !== revB.minorNum)
                return revA.minorNum - revB.minorNum;
              return revA.amendChar.localeCompare(revB.amendChar);
            });
            return sorted[sorted.length - 1]?.rev || "";
          })();
    return `${state.form.procedureType} ${state.form.procedureNumber} Rev ${lastRev}`;
  })();

  return (
    <div>
      <AppBar
        subTitleAboveTitle={
          <>{localStorage.getItem("user_name")} is Creating</>
        }
        onBack={handleAppBarBack}
        title={addProcedureAppBarTitle}
        showBlackTitle={true}
        showTablesDropdown={true}
        showQuestionIcon={true}
        questionIconDummy={true}
        buttons={[
          {
            label: "Preview",
            onClick: canView ? handlePreviewClick : undefined,
            disabled: !canView,
            title: !canView
              ? "You do not have permission to preview this document"
              : undefined,
          },
          {
            label: "Create New Revision",
            onClick: canCreate ? handleSaveClick : undefined,
            disabled: !canCreate,
            title: !canCreate
              ? "You do not have permission to create revisions"
              : undefined,
          },
        ]}
      />

      <form className="procedure-form">
        <div className="form-row">
          <div className="form-group">
            <InputWithPen
              val={state.form.procedureNumber}
              label="Document Number"
              required={true}
              placeholder="Enter Document Number"
              onClickOnPencil={handleOpenModal}
              onClear={() => {
                dispatch({
                  type: ACTION_TYPES.RESET_FORM,
                });
              }}
            />
          </div>
          <div className="form-group">
            <label>Document Type (Read Only)</label>
            <input type="text" value={state.form.procedureType} readOnly />
          </div>
          <div className="form-group">
            <label>References (Separate Each Reference using Space Key)</label>
            <input
              type="text"
              placeholder="Enter References"
              value={state.form.references}
              onChange={(e) => {
                dispatch({
                  type: ACTION_TYPES.SET_FORM_DATA,
                  value: { references: e.target.value },
                });
              }}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Document Description (Read Only)</label>
            <div style={{ width: "92%" }}>
              <RenderHtml
                descriptionBox={true}
                content={state.form.description || ""}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Revision Details</label>
            <textarea
              placeholder="Enter Revision Details"
              value={state.form.revisionDetails}
              onChange={(e) => {
                dispatch({
                  type: ACTION_TYPES.SET_FORM_DATA,
                  value: { revisionDetails: e.target.value },
                });
              }}
              style={{ resize: "none" }}
            ></textarea>
          </div>
        </div>
      </form>

      {state.modals.isPartsModalOpen && (
        <>
          <PartsPopup
            setPartsModalOpen={(value) => {
              dispatch({
                type: ACTION_TYPES.SET_MODAL_STATE,
                value: { isPartsModalOpen: value },
              });
            }}
            handleSelectPart={handleSelectPart}
            items_type="is_procedure"
            disableFilter={true}
            checkRevCreated={true}
          />
        </>
      )}

      {/* Revisions Table Component */}
      <RevisionTable
        revisionData={revisionData}
        procedureNumber={state.form.procedureNumber}
        procedureType={state.form.procedureType}
        partId={state.form.partId}
        onRevisionCreated={handleRevisionCreated}
        onEditClick={onEditClick}
        revisionsForPart={revisionsForPart}
        parseRevision={parseRevision}
        sortRevisions={sortRevisions}
      />

      <CreateNewRevisionModal
        isOpen={state.modals.isModalOpen}
        onClose={() => {
          dispatch({
            type: ACTION_TYPES.SET_MODAL_STATE,
            value: { isModalOpen: false },
          });
        }}
        mode={MODAL_MODES.DRAFT}
        navigateAfterCreate={true}
        procedureData={{
          procedureNumber: state.form.procedureNumber,
          procedureType: state.form.procedureType,
          description: state.form.description,
          references: state.form.references,
          partId: state.form.partId,
          revisionDetails: state.form.revisionDetails,
          revisionData: revisionData,
          templateProcedureRevisionId: state.templateProcedureRevisionId,
        }}
        revisionOptions={revisionOptions}
        onSuccess={handleRevisionCreated}
      />

      {/* <ZoomControls
        zoomLevel={zoomLevel}
        onZoom={handleZoom}
        onManualZoom={handleManualZoom}
      /> */}
      {state.showLoader && <Loader />}
    </div>
  );
};

export default AddProcedure;
